import {
  Braces,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  File,
  FileSpreadsheet,
  FileText,
  GitBranch,
  Image,
  Loader2,
  MessageSquare,
  Play,
  Sparkles,
  Timer,
  X,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type {
  ExtractionRunDetail as ExtractionRunDetailType,
  ExtractionRunWorkflow,
  ExtractionStepItem,
  RoutingFacts,
} from '@/services/api/cases'

interface ExtractionRunDetailViewProps {
  /** Loaded run detail, or null while loading / on error. */
  data: ExtractionRunDetailType | null
  loading: boolean
  error: string | null
  /** Admin-only: surface the dispatch worker's routing reasoning panels. */
  showRouting: boolean
  onClose: () => void
}

function getFileIcon(fileType: string | null | undefined) {
  const type = fileType?.toLowerCase()
  // File-type icon palette is categorical (not semantic status), so the chromatic
  // utilities below are an intentional exception to the color guard.
  if (type === 'pdf') return <FileText className="size-4 text-red-500" /> // frontend-rules-ok
  if (type === 'xlsx' || type === 'xls' || type === 'csv')
    return <FileSpreadsheet className="size-4 text-emerald-600" /> // frontend-rules-ok
  if (type === 'docx' || type === 'doc') return <FileText className="size-4 text-blue-500" /> // frontend-rules-ok
  if (type === 'jpg' || type === 'jpeg' || type === 'png' || type === 'gif')
    return <Image className="size-4 text-purple-500" /> // frontend-rules-ok
  return <File className="size-4 text-muted-foreground" />
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}

function formatDurationMs(ms?: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.round((ms % 60_000) / 1000)
  return s ? `${m}m ${s}s` : `${m}m`
}

function etaLabel(reason: string | null, ms: number | null | undefined): string {
  if (ms != null && ms > 0) return formatDurationMs(ms)
  switch (reason) {
    case 'warming_up':
      return 'warming up…'
    case 'routing':
      return 'analyzing…'
    case 'pending':
      return 'queued'
    case 'estimating':
      return 'estimating…'
    default:
      return '—'
  }
}

type DisplayState = 'analyzing' | 'running' | 'pending' | 'done' | 'failed'

function displayState(status: string): DisplayState {
  switch (status) {
    case 'routing':
      return 'analyzing'
    case 'pipeline_selected':
    case 'pending':
      return 'pending'
    case 'running':
      return 'running'
    case 'failed':
      return 'failed'
    case 'completed':
    case 'completed_with_errors':
    case 'done':
      return 'done'
    default:
      return 'pending'
  }
}

// A document with `status='routing'` but no running route step is queued
// behind dispatch-worker concurrency, not actually being analyzed.
// Show it as Pending so the Analyzing count tracks active work.
function workflowDisplayState(wf: Pick<ExtractionRunWorkflow, 'status' | 'steps'>): DisplayState {
  const base = displayState(wf.status)
  if (base !== 'analyzing') return base
  const routeStep = wf.steps?.find(s => s.name === 'route')
  return routeStep && routeStep.status === 'running' ? 'analyzing' : 'pending'
}

function StatusIcon({ status }: { status: string }) {
  switch (displayState(status)) {
    case 'done':
      return <CheckCircle2 className="size-4 text-success" />
    case 'failed':
      return <XCircle className="size-4 text-destructive" />
    case 'running':
      return <Play className="size-4 text-warning animate-pulse" />
    case 'analyzing':
      return <Sparkles className="size-4 text-info animate-pulse" />
    default:
      return <Clock className="size-4 text-muted-foreground" />
  }
}

function WorkflowStatusBadge({ state }: { state: DisplayState }) {
  const styles: Record<DisplayState, string> = {
    done: 'bg-success/10 text-success',
    failed: 'bg-destructive/10 text-destructive',
    running: 'bg-warning/10 text-warning',
    analyzing: 'bg-info/10 text-info',
    pending: 'bg-muted text-muted-foreground',
  }
  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-xs font-medium ${styles[state]}`}
    >
      {state}
    </span>
  )
}

function StatusChip({ label, count, tone }: { label: string; count: number; tone: DisplayState }) {
  const styles: Record<DisplayState, string> = {
    done: 'bg-success-surface text-success-foreground border-success-border',
    running: 'bg-warning-surface text-warning-foreground border-warning-border',
    analyzing: 'bg-info-surface text-info-foreground border-info-border',
    pending: 'bg-muted/40 text-muted-foreground border-border',
    failed: 'bg-destructive-surface text-destructive-foreground border-destructive-border',
  }
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-2xs ${styles[tone]}`}
    >
      <span className="font-semibold tabular-nums">{count}</span>
      <span className="capitalize opacity-80">{label}</span>
    </div>
  )
}

function StepDot({ status }: { status: string }) {
  if (status === 'done') return <CheckCircle2 className="size-3 text-success shrink-0" />
  if (status === 'failed') return <XCircle className="size-3 text-destructive shrink-0" />
  if (status === 'running') return <Loader2 className="size-3 text-warning animate-spin shrink-0" />
  return <Circle className="size-3 text-muted-foreground/40 shrink-0" />
}

function StepRow({ step }: { step: ExtractionStepItem }) {
  return (
    <div className="flex items-center gap-2 text-2xs py-1 max-md:flex-wrap">
      <StepDot status={step.status} />
      <span className="font-mono text-foreground w-32 shrink-0">{step.name}</span>
      <span className="text-muted-foreground w-16 shrink-0 tabular-nums text-right">
        {formatDurationMs(step.duration_ms)}
      </span>
      <span className="text-muted-foreground capitalize">{step.status}</span>
      {step.error && (
        <span
          className="text-destructive truncate flex-1 max-md:basis-full max-md:whitespace-normal max-md:break-words"
          title={step.error}
        >
          — {step.error}
        </span>
      )}
    </div>
  )
}

function findCurrentStep(steps?: ExtractionStepItem[] | null): ExtractionStepItem | null {
  if (!steps) return null
  return steps.find(s => s.status === 'running') || null
}

/** Pluck document_type / file_type / summary out of a JSON-shaped string when
 *  the analyzer stuffed its raw JSON response into `summary` instead of
 *  lifting the fields to the top level. Mirrors the FE fallback already used
 *  in RoutingReasoning.tsx and OverviewTab.tsx. */
function parseEmbeddedAnalysis(raw: string | null | undefined): {
  document_type: string | null
  file_type: string | null
  summary: string | null
} | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed)
    return {
      document_type: typeof parsed.document_type === 'string' ? parsed.document_type : null,
      file_type: typeof parsed.file_type === 'string' ? parsed.file_type : null,
      summary:
        typeof parsed.summary === 'string' && parsed.summary.trim() ? parsed.summary.trim() : null,
    }
  } catch {
    // Truncated JSON — regex what we can.
    const out: { document_type: string | null; file_type: string | null; summary: string | null } =
      {
        document_type: null,
        file_type: null,
        summary: null,
      }
    const docTypeMatch = trimmed.match(/"document_type"\s*:\s*"([^"]+)"/)
    if (docTypeMatch) out.document_type = docTypeMatch[1]
    const fileTypeMatch = trimmed.match(/"file_type"\s*:\s*"([^"]+)"/)
    if (fileTypeMatch) out.file_type = fileTypeMatch[1]
    const summaryMatch = trimmed.match(/"summary"\s*:\s*"((?:[^"\\]|\\.)*)/)
    if (summaryMatch) {
      out.summary = summaryMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ')
    }
    return out
  }
}

function RoutingFactsPanel({ facts }: { facts: RoutingFacts }) {
  const status = facts.analysis_status ?? 'skipped'
  const statusColor =
    status === 'completed'
      ? 'text-success'
      : status === 'failed'
        ? 'text-destructive'
        : 'text-muted-foreground'
  const conf =
    typeof facts.analysis_confidence === 'number'
      ? `${Math.round(facts.analysis_confidence * 100)}%`
      : null
  const density =
    typeof facts.pdf_text_density === 'number'
      ? `${Math.round(facts.pdf_text_density)} chars/page`
      : null

  const embedded = parseEmbeddedAnalysis(facts.summary)
  const effectiveDocumentType =
    (facts.document_type && facts.document_type !== 'unknown' ? facts.document_type : null) ??
    embedded?.document_type ??
    facts.document_type ??
    null
  const effectiveFileType =
    (facts.file_type && facts.file_type !== 'unknown' ? facts.file_type : null) ??
    embedded?.file_type ??
    facts.file_type ??
    null
  const effectiveSummary = embedded?.summary ?? facts.summary ?? null
  const routingMismatch =
    effectiveDocumentType &&
    effectiveDocumentType !== facts.document_type &&
    effectiveDocumentType !== 'unknown'

  return (
    <div className="px-3 py-2 border-t border-border bg-muted/30 text-2xs">
      <div className="flex items-center gap-1.5 mb-1.5 text-muted-foreground uppercase tracking-wide text-3xs max-md:flex-wrap">
        <Sparkles className="size-2.5" />
        <span>routing reasoning</span>
        <span className={`font-mono ${statusColor}`}>· analysis: {status}</span>
        {conf && <span className="font-mono text-muted-foreground">· conf {conf}</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-2xs max-md:grid-cols-1">
        {facts.extension && (
          <div>
            <span className="text-muted-foreground">ext: </span>
            <span>{facts.extension}</span>
          </div>
        )}
        {effectiveFileType && (
          <div>
            <span className="text-muted-foreground">file_type: </span>
            <span>{effectiveFileType}</span>
          </div>
        )}
        {effectiveDocumentType && (
          <div>
            <span className="text-muted-foreground">doc_type: </span>
            <span>{effectiveDocumentType}</span>
            {routingMismatch && (
              <span
                className="ml-1 text-warning"
                title="Recovered from embedded summary JSON — routing decision used facts.document_type, which was unknown."
              >
                ⚠
              </span>
            )}
          </div>
        )}
        {density && (
          <div>
            <span className="text-muted-foreground">density: </span>
            <span>{density}</span>
          </div>
        )}
      </div>
      {effectiveSummary && (
        <div className="mt-1.5 text-foreground/80 italic">"{effectiveSummary}"</div>
      )}
      {facts.key_findings && facts.key_findings.length > 0 && (
        <ul className="mt-1.5 list-disc pl-4 text-foreground/70 space-y-0.5">
          {facts.key_findings.slice(0, 6).map((finding, i) => (
            <li key={i}>{finding}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function WorkflowCard({
  wf,
  expanded,
  onToggle,
  showRouting,
}: {
  wf: ExtractionRunWorkflow
  expanded: boolean
  onToggle: () => void
  showRouting: boolean
}) {
  const totalMs = (wf.steps || []).reduce((sum, s) => sum + (s.duration_ms || 0), 0)
  const doneCount = (wf.steps || []).filter(s => s.status === 'done').length
  const stepCount = wf.steps?.length || 0
  const pct = stepCount > 0 ? Math.round((doneCount / stepCount) * 100) : 0
  const currentStep = findCurrentStep(wf.steps)
  const state = workflowDisplayState(wf)
  const isRunning = state === 'running'
  const isAnalyzing = state === 'analyzing'
  const isFailed = state === 'failed'

  return (
    <div
      className={`rounded-lg border ${
        isFailed
          ? 'border-destructive/30'
          : isRunning
            ? 'border-warning/30'
            : isAnalyzing
              ? 'border-info/30'
              : 'border-border'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-muted/30 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="size-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="size-3.5 text-muted-foreground shrink-0" />
        )}
        {getFileIcon(wf.file_type)}
        <div className="min-w-0 flex-1 text-left">
          <div className="text-xs text-foreground truncate">
            {wf.filename || wf.document_id || 'Unknown'}
          </div>
          {wf.pipeline && (
            <div className="text-3xs text-muted-foreground flex items-center gap-1 mt-0.5 font-mono">
              <GitBranch className="size-2.5" />
              <span className="truncate">{wf.pipeline}</span>
            </div>
          )}
          {isRunning && currentStep && (
            <div className="text-3xs text-warning flex items-center gap-1 mt-0.5">
              <Loader2 className="size-2.5 animate-spin" />
              <span>now: {currentStep.name}</span>
            </div>
          )}
          {isAnalyzing && (
            <div className="text-3xs text-info flex items-center gap-1 mt-0.5">
              <Loader2 className="size-2.5 animate-spin" />
              <span>analyzing document…</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <div className="flex items-center gap-2">
            {wf.eta_ms != null && wf.eta_ms > 0 && isRunning && (
              <span className="text-3xs text-warning tabular-nums flex items-center gap-1">
                <Timer className="size-2.5" />
                {formatDurationMs(wf.eta_ms)}
              </span>
            )}
            <WorkflowStatusBadge state={state} />
          </div>
          <div className="text-3xs text-muted-foreground tabular-nums">
            {doneCount}/{stepCount} · {formatDurationMs(totalMs)}
          </div>
        </div>
      </button>
      {isRunning && (
        <div className="h-0.5 w-full bg-muted overflow-hidden">
          <div
            className="h-full bg-warning transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      {isAnalyzing && (
        <div className="h-0.5 w-full bg-muted overflow-hidden">
          <div className="h-full w-full bg-info/60 animate-pulse" />
        </div>
      )}
      {expanded && wf.steps && (
        <div className="px-3 pt-1 pb-2 border-t border-border bg-muted/20">
          {wf.steps.map(step => (
            <StepRow key={step.name} step={step} />
          ))}
        </div>
      )}
      {expanded && showRouting && wf.routing_facts && (
        <RoutingFactsPanel facts={wf.routing_facts} />
      )}
    </div>
  )
}

const STATUS_GROUP_ORDER: Record<DisplayState, number> = {
  analyzing: 0,
  running: 1,
  pending: 2,
  failed: 3,
  done: 4,
}

function groupWorkflowsByStatus(
  workflows: ExtractionRunWorkflow[]
): Array<[DisplayState, ExtractionRunWorkflow[]]> {
  const groups: Partial<Record<DisplayState, ExtractionRunWorkflow[]>> = {}
  for (const wf of workflows) {
    const state = workflowDisplayState(wf)
    if (!groups[state]) groups[state] = []
    groups[state]!.push(wf)
  }
  return (Object.entries(groups) as Array<[DisplayState, ExtractionRunWorkflow[]]>).sort(
    (a, b) => (STATUS_GROUP_ORDER[a[0]] ?? 99) - (STATUS_GROUP_ORDER[b[0]] ?? 99)
  )
}

/**
 * Pure presentation of a single extraction run's detail panel: progress bar,
 * per-status chips, timing tiles, schema/instructions, and the grouped
 * per-document workflow drilldown. All data is provided via props; the
 * fetch/polling lives in the connected `features/documents/ExtractionRunDetail`.
 * Owns only local UI state (which workflow cards are expanded).
 */
export function ExtractionRunDetailView({
  data,
  loading,
  error,
  showRouting,
  onClose,
}: ExtractionRunDetailViewProps) {
  const { t } = useTranslation()
  const [expandedJobs, setExpandedJobs] = useState<Record<string, boolean>>({})

  const grouped = useMemo(() => (data ? groupWorkflowsByStatus(data.workflows) : []), [data])
  const displayCounts = useMemo<Record<DisplayState, number>>(() => {
    const counts: Record<DisplayState, number> = {
      analyzing: 0,
      running: 0,
      pending: 0,
      done: 0,
      failed: 0,
    }
    if (data) for (const wf of data.workflows) counts[workflowDisplayState(wf)] += 1
    return counts
  }, [data])

  useEffect(() => {
    if (!data) return
    setExpandedJobs(prev => {
      const next = { ...prev }
      let changed = false
      for (const wf of data.workflows) {
        const key = wf.job_id || wf.document_id || ''
        if (wf.status === 'running' && !(key in prev)) {
          next[key] = true
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [data])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <p className="text-sm text-destructive">{error || 'Not found'}</p>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    )
  }

  const isRunning = data.status === 'running'
  const title = data.schema_name || t('extraction.extraction')

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <StatusIcon status={data.status} />
          <h3 className="text-sm font-semibold truncate">{title}</h3>
          {data.schema_version && (
            <Badge
              variant="outline"
              className="text-3xs font-mono uppercase tracking-wider text-muted-foreground"
            >
              {data.schema_version}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" className="size-9 shrink-0 md:size-7" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-5">
        <div className="space-y-2">
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                data.status === 'failed'
                  ? 'bg-destructive'
                  : data.failed_documents > 0
                    ? 'bg-warning'
                    : isRunning
                      ? 'bg-primary'
                      : 'bg-success'
              }`}
              style={{ width: `${data.progress_percent}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {data.completed_documents + data.failed_documents}/{data.total_documents}{' '}
              {t('extraction.processed')}
              {data.failed_documents > 0 && (
                <span className="text-destructive ml-1.5">
                  {data.failed_documents} {t('extraction.failedDocs')}
                </span>
              )}
            </span>
            <span className="tabular-nums">{data.progress_percent}%</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <StatusChip label="done" count={displayCounts.done} tone="done" />
          {displayCounts.analyzing > 0 && (
            <StatusChip label="analyzing" count={displayCounts.analyzing} tone="analyzing" />
          )}
          {displayCounts.running > 0 && (
            <StatusChip label="running" count={displayCounts.running} tone="running" />
          )}
          {displayCounts.pending > 0 && (
            <StatusChip label="pending" count={displayCounts.pending} tone="pending" />
          )}
          {displayCounts.failed > 0 && (
            <StatusChip label="failed" count={displayCounts.failed} tone="failed" />
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded-md border border-border px-2 py-2">
            <div className="text-3xs uppercase tracking-wider text-muted-foreground">
              {isRunning ? 'Elapsed' : 'Total'}
            </div>
            <div className="font-mono text-sm text-foreground tabular-nums mt-0.5">
              {formatDurationMs(data.elapsed_ms)}
            </div>
          </div>
          <div className="rounded-md border border-border px-2 py-2">
            <div className="text-3xs uppercase tracking-wider text-muted-foreground">ETA</div>
            <div className="font-mono text-sm text-foreground tabular-nums mt-0.5">
              {isRunning ? etaLabel(data.eta_reason, data.eta_ms) : '—'}
            </div>
          </div>
          <div className="rounded-md border border-border px-2 py-2">
            <div className="text-3xs uppercase tracking-wider text-muted-foreground">Started</div>
            <div className="text-2xs text-foreground mt-0.5">{formatDate(data.started_at)}</div>
          </div>
        </div>

        <div className="space-y-2">
          {data.schema_name && (
            <div className="flex items-start gap-2 text-xs">
              <Braces className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <span className="text-muted-foreground">{t('extraction.detail.schema')}: </span>
                <span className="text-foreground">{data.schema_name}</span>
                <span className="ml-1.5 text-3xs font-mono uppercase tracking-wider text-muted-foreground/70">
                  {data.schema_version || 'v—'}
                </span>
              </div>
            </div>
          )}
          <div className="flex items-start gap-2 text-xs">
            <MessageSquare className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <span className="text-muted-foreground">{t('extraction.instructions')}: </span>
              {data.instructions ? (
                <span className="text-foreground whitespace-pre-wrap">{data.instructions}</span>
              ) : (
                <span className="text-muted-foreground/70 italic">—</span>
              )}
            </div>
          </div>
        </div>

        {data.workflows.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('extraction.detail.documents', { count: data.workflows.length })}
            </h4>
            {grouped.map(([status, items]) => (
              <div key={status} className="space-y-1">
                <div className="flex items-center gap-2 text-3xs uppercase tracking-wider text-muted-foreground pl-1">
                  <span>{status}</span>
                  <span className="tabular-nums">({items.length})</span>
                </div>
                {items.map(wf => {
                  const key = wf.job_id || wf.document_id || ''
                  return (
                    <WorkflowCard
                      key={key}
                      wf={wf}
                      expanded={!!expandedJobs[key]}
                      onToggle={() => setExpandedJobs(prev => ({ ...prev, [key]: !prev[key] }))}
                      showRouting={showRouting}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
